import { useMutation, useReactiveVar } from '@apollo/client';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Button } from '@mui/material';
import Box from '@mui/material/Box/Box';
import Checkbox from '@mui/material/Checkbox';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import type React from 'react';
import { useState } from 'react';
import { type CreateSharedLinkResponse, type CreateSharedLinkVariables, createSharedLink } from 'src/api/sharedLinks';
import { ASSET, COLLECTION, EMPTY_STRING, ONE_DAY, ONE_HOUR, ONE_MONTH, ONE_WEEK } from 'src/constants';
import viewStateVar from 'src/store/viewState';
import * as Yup from 'yup';
import AkomiDialog from './AkomiDialog';
import AkomiDialogActions from './AkomiDialogActions';

interface FormValues {
  viewLimit: number | null;
  expiration: number | string;
  password: string | null;
  downloadable: boolean;
}

const ShareDialog: React.FC = () => {
  const [createLink] = useMutation<CreateSharedLinkResponse, CreateSharedLinkVariables>(createSharedLink);
  const viewState = useReactiveVar(viewStateVar);
  const [disablePassword, setDisablePassword] = useState<boolean>(true);
  const [disableViewLimit, setDisableViewLimit] = useState<boolean>(true);
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [sharedLink, setSharedLink] = useState<string | null>(null);
  const [createLinkError, setCreateLinkError] = useState<string>('');
  const expirationChoices = ['One hour', 'One day', 'One week', 'One month', 'Never'];

  const onClose = (): void => {
    viewStateVar({
      ...viewStateVar(),
      shareDialog: null,
      shareContent: null,
    });
  };

  const expirationString = (delta: number | string): string => {
    if (delta !== EMPTY_STRING) {
      // Create expiration datetime string in ISO format
      const date = new Date(Date.now() + (delta as number));
      return date.toISOString();
    } else {
      return EMPTY_STRING;
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      viewLimit: null,
      expiration: ONE_WEEK,
      password: null,
      downloadable: false,
    },
    validationSchema: Yup.object({
      viewLimit: Yup.number().nullable().min(1, 'View limit must be greater than zero'),
      password: Yup.string().nullable().min(4, 'Password must be at least 4 characters long'),
    }),
    onSubmit: async (values) => {
      const expiration = expirationString(values.expiration);
      const variables: CreateSharedLinkVariables = {
        viewLimit: values.viewLimit ?? undefined,
        expiration,
        password: values.password ?? undefined,
        downloadable: values.downloadable,
        assetId: viewState.shareContent === ASSET ? (viewState.shareDialog as string) : undefined,
        collectionId: viewState.shareContent === COLLECTION ? (viewState.shareDialog as string) : undefined,
      };
      createLink({ variables })
        .then((resp) => {
          const relUrl = resp.data?.createSharedLink.sharedLink;
          if (relUrl) {
            const url = window.location.origin + relUrl;
            setSharedLink(url);
          }
        })
        .catch((e: Error) => {
          setCreateLinkError(e.message);
        });
    },
  });

  const generatePassword = (length: number): void => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890!%&*[@#?]';
    let ret = '';
    for (let i = 0; i < length; i++) {
      ret += chars[Math.floor(Math.random() * chars.length)];
    }
    formik.setFieldValue('password', ret);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const togglePasswordVisibility = (): void => {
    setPasswordVisibility(!passwordVisibility);
  };

  const missingValues = (): boolean => {
    return (!disablePassword && !formik.values.password) || (!disableViewLimit && !formik.values.viewLimit);
  };

  const mapExpirationChoices = (): React.ReactNode[] => {
    return expirationChoices.map((i) => {
      if (i === 'One hour') {
        return (
          <MenuItem key="hour" value={ONE_HOUR}>
            {i}
          </MenuItem>
        );
      } else if (i === 'One day') {
        return (
          <MenuItem key="day" value={ONE_DAY}>
            {i}
          </MenuItem>
        );
      } else if (i === 'One week') {
        return (
          <MenuItem key="week" value={ONE_WEEK}>
            {i}
          </MenuItem>
        );
      } else if (i === 'One month') {
        return (
          <MenuItem key="month" value={ONE_MONTH}>
            {i}
          </MenuItem>
        );
      } else {
        return (
          <MenuItem key="never" value={EMPTY_STRING}>
            {i}
          </MenuItem>
        );
      }
    });
  };

  const copyText = (txt: string): void => {
    navigator.clipboard.writeText(txt);
  };

  return (
    <AkomiDialog
      open={!!viewState.shareDialog}
      onClose={onClose}
      title="Share"
      maxWidth="xs"
      actions={
        !sharedLink ? (
          <AkomiDialogActions variant="confirm">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              id="cyp-share-get-link-button"
              disabled={!!formik.errors.password || !!formik.errors.viewLimit || missingValues()}
              onClick={() => formik.submitForm()}
            >
              GET LINK
            </Button>
          </AkomiDialogActions>
        ) : undefined
      }
    >
      {!sharedLink && (
        <form onSubmit={formik.handleSubmit}>
          {/* Expiration Time */}
          <Box sx={{ mb: 2 }}>
            <FormLabel sx={{ display: 'block', fontSize: '13px', color: 'text.secondary', mb: '6px' }}>
              Expiration Time
            </FormLabel>
            <Select
              fullWidth
              size="small"
              {...formik.getFieldProps('expiration')}
              displayEmpty
              renderValue={(selected) => {
                if (selected === ONE_HOUR) return 'One hour';
                if (selected === ONE_DAY) return 'One day';
                if (selected === ONE_WEEK) return 'One week';
                if (selected === ONE_MONTH) return 'One month';
                if (selected === '') return 'Never';
                return '';
              }}
            >
              {mapExpirationChoices()}
            </Select>
          </Box>

          {/* Downloadable */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Checkbox
              id="cyp-share-downloadable-checkbox"
              {...formik.getFieldProps('downloadable')}
              checked={formik.values.downloadable}
              size="small"
            />
            <Typography variant="body2">Downloadable</Typography>
          </Box>

          {/* Require Password */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Checkbox checked={!disablePassword} onChange={() => setDisablePassword(!disablePassword)} size="small" />
            <Typography variant="body2">Require Password</Typography>
          </Box>

          {/* Password field — only visible when password enabled */}
          {!disablePassword && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Password"
                type={passwordVisibility ? 'text' : 'password'}
                variant="standard"
                fullWidth
                size="small"
                {...formik.getFieldProps('password')}
                value={formik.values.password ?? ''}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {passwordVisibility ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="secondary"
                size="small"
                sx={{ mt: 1, textTransform: 'none', fontSize: '13px' }}
                onClick={() => generatePassword(16)}
              >
                Generate Password
              </Button>
            </Box>
          )}

          {createLinkError && (
            <Typography color="error" sx={{ fontSize: '13px' }}>
              {createLinkError}
            </Typography>
          )}
        </form>
      )}
      {!!sharedLink && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 500, mb: '4px' }}>Link</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                id="cyp-shared-link-span"
                sx={{ wordBreak: 'break-all', fontSize: '13px', color: (theme) => theme.palette.primary.light }}
              >
                {sharedLink}
              </Typography>
              <IconButton aria-label="copy link" onClick={() => copyText(sharedLink)} size="small">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {formik.values.password && (
            <Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 500, mb: '4px' }}>
                Password
                <IconButton
                  aria-label="copy password"
                  onClick={() => copyText(formik.values.password as string)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </AkomiDialog>
  );
};

export default ShareDialog;
